import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleNumber,
  formatLocalePercent,
} from "./format";
import {
  DEFAULT_UI_LOCALE,
  UI_LOCALE_CHANGE_EVENT,
  UI_LOCALE_CHANNEL_NAME,
  UI_LOCALE_STORAGE_KEY,
  canAccessUiLocaleStorage,
  browserLanguageTags,
  compareUiLocalePreferences,
  isUiLocale,
  languageTagForUiLocale,
  parseStoredUiLocalePreference,
  readUiLocalePreference,
  uiLocaleFromLanguageTags,
  writeUiLocalePreference,
  type UiLocale,
  type UiLocalePreference,
} from "./locale";
import { syncNativeUiLocale } from "../platform/tauriShell";
import { applyDocumentUiLocale } from "./document";

const UI_LOCALE_HANDSHAKE_TIMEOUT_MS = 100;

interface UiLocaleChangeDetail extends UiLocalePreference {
  readonly persisted: boolean;
}

interface UiLocaleStateRequest {
  readonly type: "request-state";
  readonly requestId: string;
}

interface UiLocaleStateResponse {
  readonly type: "state";
  readonly requestId: string;
  readonly preference: UiLocalePreference;
}

interface ParsedUiLocaleState extends UiLocalePreference {
  readonly requestId: string;
}

interface UiLocaleBroadcastChange extends UiLocalePreference {
  readonly type: "change";
}

let localeHandshakeSequence = 0;
let localeSourceSequence = 0;

function nextLocaleHandshakeRequestId(): string {
  localeHandshakeSequence += 1;
  return `${Date.now().toString(36)}-${localeHandshakeSequence.toString(36)}`;
}

function nextLocaleSourceId(): string {
  localeSourceSequence += 1;
  const randomPart =
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return [
    Date.now().toString(36),
    localeSourceSequence.toString(36),
    randomPart,
  ].join("-");
}

function nextLocalePreference(
  locale: UiLocale,
  currentUpdatedAt: number,
  sourceId: string,
): UiLocalePreference {
  return {
    locale,
    updatedAt: Math.max(Date.now(), currentUpdatedAt + 1),
    sourceId,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function preferenceFromUnknown(
  value: unknown,
): UiLocalePreference | null {
  if (
    !isRecord(value) ||
    !isUiLocale(value.locale) ||
    typeof value.updatedAt !== "number" ||
    !Number.isFinite(value.updatedAt)
  ) {
    return null;
  }
  return {
    locale: value.locale,
    updatedAt: value.updatedAt,
    sourceId:
      typeof value.sourceId === "string" && value.sourceId
        ? value.sourceId
        : "legacy",
  };
}

function requestFromUnknown(
  value: unknown,
): UiLocaleStateRequest | null {
  return isRecord(value) &&
    value.type === "request-state" &&
    typeof value.requestId === "string"
    ? {
        type: "request-state",
        requestId: value.requestId,
      }
    : null;
}

function stateFromUnknown(
  value: unknown,
): ParsedUiLocaleState | null {
  if (
    !isRecord(value) ||
    value.type !== "state" ||
    typeof value.requestId !== "string"
  ) {
    return null;
  }
  const preference =
    preferenceFromUnknown(value.preference) ??
    preferenceFromUnknown(value);
  return preference
    ? {
        requestId: value.requestId,
        ...preference,
      }
    : null;
}

function changeFromUnknown(
  value: unknown,
): UiLocaleBroadcastChange | null {
  const preference = preferenceFromUnknown(value);
  return preference && isRecord(value) && value.type === "change"
    ? {
        type: "change",
        ...preference,
      }
    : null;
}

export type UiLocalePersistence = "persisted" | "unavailable";

export interface I18nContextValue {
  readonly locale: UiLocale;
  readonly localePersistence: UiLocalePersistence;
  readonly languageTag: string;
  readonly setLocale: (locale: UiLocale) => void;
  readonly formatNumber: (
    value: number,
    options?: Intl.NumberFormatOptions,
  ) => string;
  readonly formatCurrency: (
    value: number,
    currency: string,
    options?: Omit<
      Intl.NumberFormatOptions,
      "style" | "currency"
    >,
  ) => string;
  readonly formatPercent: (
    percentagePoints: number,
    fractionDigits?: number,
    signDisplay?: Intl.NumberFormatOptions["signDisplay"],
  ) => string;
  readonly formatDate: (
    value: Date | number,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
}

const FALLBACK_CONTEXT: I18nContextValue = {
  locale: DEFAULT_UI_LOCALE,
  localePersistence: "persisted",
  languageTag: languageTagForUiLocale(DEFAULT_UI_LOCALE),
  setLocale: () => undefined,
  formatNumber: (value, options) =>
    formatLocaleNumber(DEFAULT_UI_LOCALE, value, options),
  formatCurrency: (value, currency, options) =>
    formatLocaleCurrency(
      DEFAULT_UI_LOCALE,
      value,
      currency,
      options,
    ),
  formatPercent: (
    value,
    fractionDigits,
    signDisplay,
  ) =>
    formatLocalePercent(
      DEFAULT_UI_LOCALE,
      value,
      fractionDigits,
      signDisplay,
    ),
  formatDate: (value, options) =>
    formatLocaleDate(DEFAULT_UI_LOCALE, value, options),
};

const I18nContext =
  createContext<I18nContextValue>(FALLBACK_CONTEXT);

export interface I18nProviderProps {
  readonly children: ReactNode;
  readonly initialLocale?: UiLocale;
}

export function I18nProvider({
  children,
  initialLocale,
}: I18nProviderProps) {
  const [localeSourceId] = useState(nextLocaleSourceId);
  const [initialization] = useState(() => {
    const windowTarget =
      typeof window === "undefined" ? undefined : window;
    const preference =
      initialLocale === undefined
        ? readUiLocalePreference(windowTarget)
        : {
            locale: initialLocale,
            updatedAt: 0,
            sourceId: localeSourceId,
          };
    const persistence: UiLocalePersistence =
      windowTarget === undefined ||
      canAccessUiLocaleStorage(windowTarget)
        ? "persisted"
        : "unavailable";
    return {
      preference,
      locale:
        preference?.locale ??
        uiLocaleFromLanguageTags(
          browserLanguageTags(
            typeof navigator === "undefined" ? undefined : navigator,
          ),
        ),
      persistence,
    };
  });
  const [locale, setLocaleState] = useState<UiLocale>(
    initialization.locale,
  );
  const [localePersistence, setLocalePersistence] =
    useState<UiLocalePersistence>(initialization.persistence);
  const channelAvailable =
    typeof window !== "undefined" &&
    typeof BroadcastChannel !== "undefined";
  const [nativeLocaleReady, setNativeLocaleReady] = useState(
    !channelAvailable,
  );
  const channelRef = useRef<BroadcastChannel | null>(null);
  const localePreferenceRef = useRef<UiLocalePreference>(
    initialization.preference ?? {
      locale: initialization.locale,
      updatedAt: 0,
      sourceId: localeSourceId,
    },
  );
  const canRespondToStateRequestRef = useRef(
    initialization.preference !== null,
  );
  const nativeLocaleReadyRef = useRef(!channelAvailable);
  const pendingStateRequestIdRef = useRef<string | null>(null);

  const markNativeLocaleReady = useCallback(() => {
    canRespondToStateRequestRef.current = true;
    if (nativeLocaleReadyRef.current) {
      return;
    }
    nativeLocaleReadyRef.current = true;
    setNativeLocaleReady(true);
  }, []);

  const finishLocaleHydration = useCallback(() => {
    pendingStateRequestIdRef.current = null;
    markNativeLocaleReady();
  }, [markNativeLocaleReady]);

  const applyLocalePreference = useCallback(
    (
      preference: UiLocalePreference,
      {
        force = false,
        persist = false,
      }: {
        readonly force?: boolean;
        readonly persist?: boolean;
      } = {},
    ): boolean => {
      if (
        !force &&
        compareUiLocalePreferences(
          preference,
          localePreferenceRef.current,
        ) < 0
      ) {
        return false;
      }
      const normalizedPreference: UiLocalePreference = {
        locale: preference.locale,
        updatedAt: preference.updatedAt,
        sourceId: preference.sourceId,
      };
      localePreferenceRef.current = normalizedPreference;
      canRespondToStateRequestRef.current = true;
      setLocaleState(normalizedPreference.locale);
      if (persist && typeof window !== "undefined") {
        setLocalePersistence(
          writeUiLocalePreference(window, normalizedPreference)
            ? "persisted"
            : "unavailable",
        );
      }
      return true;
    },
    [],
  );

  const setLocale = useCallback(
    (nextLocale: UiLocale) => {
      const preference = nextLocalePreference(
        nextLocale,
        localePreferenceRef.current.updatedAt,
        localeSourceId,
      );
      applyLocalePreference(preference, { force: true });
      if (typeof window === "undefined") {
        return;
      }

      const persisted = writeUiLocalePreference(window, preference);
      setLocalePersistence(
        persisted ? "persisted" : "unavailable",
      );
      window.dispatchEvent(
        new CustomEvent<UiLocaleChangeDetail>(
          UI_LOCALE_CHANGE_EVENT,
          {
            detail: {
              ...preference,
              persisted,
            },
          },
        ),
      );
      try {
        channelRef.current?.postMessage({
          type: "change",
          ...preference,
        } satisfies UiLocaleBroadcastChange);
      } catch {
        // Storage and the in-window event still keep supported contexts in sync.
      }
      finishLocaleHydration();
    },
    [
      applyLocalePreference,
      finishLocaleHydration,
      localeSourceId,
    ],
  );

  useEffect(() => {
    applyDocumentUiLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== UI_LOCALE_STORAGE_KEY) {
        return;
      }
      const eventPreference =
        parseStoredUiLocalePreference(event.newValue);
      const storedPreference = readUiLocalePreference(window);
      if (!eventPreference && !storedPreference) {
        applyLocalePreference(
          nextLocalePreference(
            DEFAULT_UI_LOCALE,
            localePreferenceRef.current.updatedAt,
            localeSourceId,
          ),
          { force: true },
        );
        setLocalePersistence("persisted");
        finishLocaleHydration();
        return;
      }

      const winner = [
        localePreferenceRef.current,
        eventPreference,
        storedPreference,
      ]
        .filter(
          (candidate): candidate is UiLocalePreference =>
            candidate !== null,
        )
        .reduce((latest, candidate) =>
          compareUiLocalePreferences(candidate, latest) > 0
            ? candidate
            : latest
        );
      applyLocalePreference(winner);

      const storageAlreadyHasWinner =
        storedPreference !== null &&
        compareUiLocalePreferences(
          storedPreference,
          winner,
        ) === 0;
      setLocalePersistence(
        storageAlreadyHasWinner ||
          writeUiLocalePreference(window, winner)
          ? "persisted"
          : "unavailable",
      );
      finishLocaleHydration();
    };
    const onLocaleChange = (event: Event) => {
      const localeEvent =
        event as CustomEvent<UiLocaleChangeDetail>;
      const preference = preferenceFromUnknown(
        localeEvent.detail,
      );
      if (preference) {
        applyLocalePreference(preference);
        setLocalePersistence(
          localeEvent.detail.persisted
            ? "persisted"
            : "unavailable",
        );
        finishLocaleHydration();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(
      UI_LOCALE_CHANGE_EVENT,
      onLocaleChange,
    );

    let channel: BroadcastChannel | null = null;
    let handshakeTimer: number | null = null;
    let requestId: string | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        channel = new BroadcastChannel(
          UI_LOCALE_CHANNEL_NAME,
        );
        channel.onmessage = (
          event: MessageEvent<unknown>,
        ) => {
          const request = requestFromUnknown(event.data);
          if (request) {
            if (!canRespondToStateRequestRef.current) {
              return;
            }
            try {
              channel?.postMessage({
                type: "state",
                requestId: request.requestId,
                preference: localePreferenceRef.current,
              } satisfies UiLocaleStateResponse);
            } catch {
              // The requester will finish its bounded handshake on timeout.
            }
            return;
          }

          const state = stateFromUnknown(event.data);
          if (state) {
            if (
              state.requestId !==
              pendingStateRequestIdRef.current
            ) {
              return;
            }
            applyLocalePreference(state, { persist: true });
            return;
          }

          const change = changeFromUnknown(event.data);
          if (change) {
            if (
              applyLocalePreference(change, { persist: true })
            ) {
              // A live change can update native chrome immediately, while
              // the pending handshake remains open for a newer state reply.
              markNativeLocaleReady();
            }
            return;
          }

          // Versions before the handshake protocol broadcast only { locale }.
          if (
            isRecord(event.data) &&
            isUiLocale(event.data.locale)
          ) {
            applyLocalePreference(
              nextLocalePreference(
                event.data.locale,
                localePreferenceRef.current.updatedAt,
                localeSourceId,
              ),
              { force: true, persist: true },
            );
            finishLocaleHydration();
          }
        };
        channelRef.current = channel;
        requestId = nextLocaleHandshakeRequestId();
        pendingStateRequestIdRef.current = requestId;
        channel.postMessage({
          type: "request-state",
          requestId,
        } satisfies UiLocaleStateRequest);
        handshakeTimer = window.setTimeout(() => {
          if (
            pendingStateRequestIdRef.current === requestId
          ) {
            markNativeLocaleReady();
          }
        }, UI_LOCALE_HANDSHAKE_TIMEOUT_MS);
      } catch {
        channel?.close();
        if (channelRef.current === channel) {
          channelRef.current = null;
        }
        channel = null;
        finishLocaleHydration();
      }
    } else {
      finishLocaleHydration();
    }

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(
        UI_LOCALE_CHANGE_EVENT,
        onLocaleChange,
      );
      if (handshakeTimer !== null) {
        window.clearTimeout(handshakeTimer);
      }
      if (
        requestId !== null &&
        pendingStateRequestIdRef.current === requestId
      ) {
        pendingStateRequestIdRef.current = null;
      }
      channel?.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [
    applyLocalePreference,
    finishLocaleHydration,
    localeSourceId,
    markNativeLocaleReady,
  ]);

  useEffect(() => {
    if (!nativeLocaleReady) {
      return;
    }
    void syncNativeUiLocale(locale);
  }, [locale, nativeLocaleReady]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      localePersistence,
      languageTag: languageTagForUiLocale(locale),
      setLocale,
      formatNumber: (number, options) =>
        formatLocaleNumber(locale, number, options),
      formatCurrency: (number, currency, options) =>
        formatLocaleCurrency(
          locale,
          number,
          currency,
          options,
        ),
      formatPercent: (
        number,
        fractionDigits,
        signDisplay,
      ) =>
        formatLocalePercent(
          locale,
          number,
          fractionDigits,
          signDisplay,
        ),
      formatDate: (date, options) =>
        formatLocaleDate(locale, date, options),
    }),
    [locale, localePersistence, setLocale],
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

// Context and provider intentionally share one module so every window uses
// the same fallback contract.
// eslint-disable-next-line react-refresh/only-export-components
export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
